import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  CircularProgress,
  useMediaQuery,
  useTheme
} from '@mui/material';
import axios from 'axios';
import { API_BASE_URL } from '../constants/config';

interface InventoryStatusPerShop {
  itemName: string;
  quantityUnit: string;
  totalAdded: number;
  consumable: number;
  blockedduetoinventoryrequests: number;
  category: string;
  lostatfactoy: number;
  soldatfactoy: number;
  stockValueAtManufacturingPriceFactory: number;
  stockValueAtSellingPriceFactory: number;
  sumOfStockValueAtManufacturingPrice: number;
  sumOfStockValueAtSellingPrice: number;
  manufacturingPricePerUnit: number;
  unsoldatshops: { [shopName: string]: number };
  soldatshops: { [shopName: string]: number };
  lostatshop: { [shopName: string]: number };
  stockValueAtManufacturingPriceShops: { [shopName: string]: number };
  stockValueAtSellingPriceShops: { [shopName: string]: number };
}

type Order = 'asc' | 'desc';

const DataView: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const token = localStorage.getItem('token');
  
  const [inventoryStatusPerShop, setInventoryStatusPerShop] = useState<InventoryStatusPerShop[]>([]);
  const [isLoadingInventoryStatusPerShop, setIsLoadingInventoryStatusPerShop] = useState(false);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<string>('itemName');

  useEffect(() => {
    if (token) {
      fetchInventoryStatusPerShop();
    }
  }, [token]);

  const fetchInventoryStatusPerShop = async () => {
    setIsLoadingInventoryStatusPerShop(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/inventory-transactions/status/per-shop`, {
        headers: { Authorization: token }
      });
      setInventoryStatusPerShop(response.data);
    } catch (err: any) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        console.error('Error fetching inventory status:', err);
      }
    } finally {
      setIsLoadingInventoryStatusPerShop(false);
    }
  };

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Filter items to only show Box or Bag categories
  const filteredItems = inventoryStatusPerShop.filter(item => 
    item.category === 'Box' || item.category === 'Bag'
  );

  // Sort the filtered items
  const sortedItems = [...filteredItems].sort((a, b) => {
    let aValue: any;
    let bValue: any;
    
    switch (orderBy) {
      case 'itemName':
        aValue = a.itemName;
        bValue = b.itemName;
        break;
      case 'consumable':
        aValue = a.consumable;
        bValue = b.consumable;
        break;
      default:
        aValue = a[orderBy as keyof InventoryStatusPerShop];
        bValue = b[orderBy as keyof InventoryStatusPerShop];
    }
    
    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }
    
    if (order === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  if (isLoadingInventoryStatusPerShop) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <TableContainer 
        component={Paper} 
        sx={{ 
          maxHeight: 'calc(100vh - 300px)',
          overflowX: 'auto',
          '& .MuiTableCell-root': {
            padding: '8px 16px',
            fontSize: '0.875rem'
          }
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: '200px' }}>
                <TableSortLabel
                  active={orderBy === 'itemName'}
                  direction={orderBy === 'itemName' ? order : 'asc'}
                  onClick={() => handleSort('itemName')}
                >
                  Item Name
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ minWidth: '150px' }}>
                <TableSortLabel
                  active={orderBy === 'consumable'}
                  direction={orderBy === 'consumable' ? order : 'asc'}
                  onClick={() => handleSort('consumable')}
                >
                  Remaining at Factory
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={2} align="center">
                  No items found with Box or Bag category
                </TableCell>
              </TableRow>
            ) : (
              sortedItems.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>{item.itemName}</TableCell>
                  <TableCell align="right">
                    {Math.round(item.consumable)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default DataView;

